import { useEffect, useMemo, useReducer, useRef, useState } from "react"
import uniqBy from "lodash/uniqBy"
import moment from "moment"
import classNames from "classnames"

import BackgroundColorContext from "./context"

import Spinner from "shared/components/spinner"
import ServiceSelector from "./service_selector"
import StepSelector from "./step_selector"
import ServiceConnection from "./service_connection"
import Preview from "./preview"
import FilterCode from "./filter_code"
import Delay from "./delay"
import { creationModeOptions, previousStep, steps, stepsTitle } from "./steps"
import { formatStep, initialState, reducer } from "./reducer"
import Header from "./header"
import ChatTab from "./conversation/chat_tab"
import type { IntroCopy } from "./conversation/intro"
import type { UpgradeRequest } from "./conversation/types"
import InitialStep from "./initial_step"
import StepFieldsSelector from "./step_fields_selector"
import {
  AppletDataStoreQuery,
  DIYLoadServiceActionsQuery,
  DIYLoadServiceQueriesQuery,
  DIYLoadServiceTriggersQuery,
} from "./queries"
import AppletLimitWarning from "./applet_limit_warning"
import CreditBalanceBar from "./credit_balance_bar"
import MakeItYourOwnModal from "./make_it_your_own_modal"
import UpgradePlanModal from "../upgrade_plan_modal"

import {
  trackAppletCreationStarted,
  trackAppletCreationCompleted,
  trackAppletCreationFailed,
  trackAppletCreatePreviewShown,
  type AppletCreationMode,
  type ConversationEntryPoint,
} from "app/utils/mixpanel_tracking"
import {
  consumePendingPrompt,
  type PendingPromptEntry,
  type PendingPromptSource,
} from "app/utils/pending_prompt_storage"

const ENTRY_POINT_BY_PROMPT_SOURCE: Record<PendingPromptSource, ConversationEntryPoint> = {
  explore_prompt_hero: "explore_tab",
  home_prompt_hero: "homepage_prompt",
}

import colors from "foundation/_colors_export.scss?export"

import styles from "./index.module.scss"
import {
  fetchCredits,
  fetchStepDefaultValues,
  fetchStepFieldsAndIngredients,
  loadPreview,
  loadPrivateServices,
} from "./api"
import { creditsLoaded } from "./utils/credits"
import { ingredientsMetadataFromState } from "./utils/ingredients_metadata_from_state"
import { parseFilterCodeDataStore } from "./utils/parse_filter_code_data_store"
import { graphQueryBang } from "shared/lib/graphql"
import { selectedActionsAboveLimit } from "./utils/selected_actions_above_limit"
import { cancellationError, needsUpgradeError } from "./utils/error_map"
import { pluralizeStepType } from "shared/lib/utils"
import { parseTemplateFromParams } from "./utils/template_parser"
import { loadTemplateData } from "./utils/load_template_data"
import { parseAnyChatPrefillFromParams } from "./utils/tqa_prefill"
import { clearStoredConversation } from "./conversation/use_chat"
import {
  NormalizedApplet,
  Action,
  Query,
  Trigger,
  User,
  StepType,
  Service,
  TQAType,
  LiveChannels,
  UIStepField,
} from "./utils/interfaces"

import ErrorBoundary from "shared/components/error_boundary"
import { canUpgradeCredits, hasProFeatures } from "shared/utils/tiers"
import type { Tier } from "shared/utils/tiers"
import FilterCodeModal from "./filter_code_modal"
import modalCopy, { CheckoutStep } from "app/components/upgrade_plan_modal/modal_copy"
import { prefetchRecommendedServiceImages, RecommendedServices } from "./utils/prefetch_recommended_service_images"

interface Props {
  importedState: {
    normalized_applet: NormalizedApplet
    id: string
    applet_trigger: { trigger: Trigger }
    applet_actions: Array<{ action: Action }>
    applet_queries: Array<{ query: Query }>
    filter_code: string
    ai_action_assist: { prompt: string } | null
  }
  filterCodeTemplate: string
  recommendedServices: RecommendedServices
  preloadedServices: Array<Service>
  allServiceCategories: Array<string>
  user: User
  urls: {
    connectionFinishedUrl: string
    newProPlusSubscriptionUrl: string
    newProSubscriptionUrl: string
    modalSubscriptionUrl: string
  }
  msa: boolean
  serviceSuggestion: string
  personalAppletsCount: number
  proPlusProductId: string
  chatAvailable?: boolean
  introCopy: IntroCopy
}

interface UpgradeToProState {
  showModal: boolean
  step: string
  stepTier?: null | string
  trackObjectId?: string
  onUpgrade?: (checkoutModalTarget: string) => void
}

interface MakeItYourOwnState {
  showModal: boolean
  onConvert?: () => void
  onCancel?: () => void
  onClose?: () => void
}

const isUserConnected = (service: Service) => service.live_channels?.some(lc => !lc.offline)

const DIY = ({
  importedState,
  filterCodeTemplate,
  allServiceCategories,
  preloadedServices,
  recommendedServices,
  urls,
  msa = false,
  serviceSuggestion,
  personalAppletsCount,
  proPlusProductId,
  chatAvailable = true,
  introCopy,
  user: propsUser,
}: Props) => {
  const [makeItYourOwn, updateMakeItYourOwn] = useState<MakeItYourOwnState>({
    showModal: false,
    onConvert: () => {},
    onCancel: () => {},
    onClose: () => {},
  })
  const [upgradeToPro, updateUpgradeToPro] = useState<UpgradeToProState>({
    showModal: false,
    step: "queries",
    stepTier: null,
    trackObjectId: "",
  })
  const [filterCodeModal, updateFilterCodeModal] = useState<{ showModal: boolean }>({
    showModal: false,
  })
  // Mounted by the header on the filter code step; FilterCode portals its CTA into it.
  const [headerActionSlot, setHeaderActionSlot] = useState<HTMLDivElement | null>(null)
  const [persistedDataStore, setPersistedDataStore] = useState<string[]>([])

  const urlParams = new URLSearchParams(window.location.search)
  // Editing an existing Applet is a Classic-composer job — chat can't edit one, and the
  // switcher is hidden while editing, so landing in chat would strand the user.
  const initialCreationMode =
    !chatAvailable || importedState || urlParams.has("classic")
      ? creationModeOptions.DIY
      : creationModeOptions.CHAT
  const [creationModeOption, updateCreationModeOption] = useState<number>(initialCreationMode)

  const templateData = parseTemplateFromParams(urlParams)
  // Try it prefill: only consumed when the chat pane will actually show.
  // useState keeps one stable identity for the page's lifetime, so ChatTab's
  // memo/effects keyed on it don't churn on every composer render.
  const [chatPrefill] = useState(() =>
    initialCreationMode === creationModeOptions.CHAT ? parseAnyChatPrefillFromParams(urlParams) : null,
  )

  // Hero hand-off (e.g. /explore): the prompt itself travels via localStorage so it
  // never lands in a URL; the ?autostart= param only carries the entry's one-time
  // token, and auto-send requires the two to match — so concurrent tabs holding an
  // older flag fall back to pre-fill. Consumption is a post-commit effect — never
  // render work, which React may replay or discard — and ChatTab only mounts after
  // the hand-off resolves, so it initializes with the final prompt. Consume-on-read
  // plus stripping the flag keeps a refresh from re-sending.
  const consumedPromptRef = useRef<PendingPromptEntry | null | undefined>(undefined)
  const [pendingHandOff, setPendingHandOff] = useState<{
    prompt: PendingPromptEntry | null
    autoStart: boolean
  } | null>(null)
  useEffect(() => {
    const url = new URL(window.location.href)
    const autostartToken = url.searchParams.get("autostart")
    if (autostartToken !== null) {
      url.searchParams.delete("autostart")
      window.history.replaceState(window.history.state, "", url)
    }
    // The ref keeps a re-run of this effect from consuming twice and losing the prompt.
    if (consumedPromptRef.current === undefined) consumedPromptRef.current = consumePendingPrompt()
    const prompt = consumedPromptRef.current
    setPendingHandOff({
      prompt,
      autoStart:
        !!autostartToken &&
        !!prompt &&
        prompt.token === autostartToken &&
        initialCreationMode === creationModeOptions.CHAT,
    })
  }, [])

  const [state, dispatch] = useReducer(reducer, {
    ...initialState({ user: propsUser }),
    loading: !!importedState || !!templateData,
    editing: !!importedState,
  })

  const user = state.user

  const userOwned = importedState?.normalized_applet?.author_id === window.App.user?.id
  // A community Applet saves through normalizedAppletEnable, which carries no AI Action Assist, until it is made the user's own.
  const canSetUpSmartFill = !!user.canUseSmartFill && (!state.editing || userOwned || state.makingItTheirOwn)

  const publishedByCurrentUser = userOwned && importedState?.normalized_applet?.published === true

  const publishedAppletData = {
    publishedAt: moment.utc(new Date(importedState?.normalized_applet?.created_at?.substring(0, 10))).format("LL"),
    enabledCount: importedState?.normalized_applet?.installs_count,
  }

  const userAtQuota = user.appletQuota.total >= 0 && user.appletQuota.remaining === 0

  const creationAttemptIdRef = useRef<string | null>(null)
  const creationAttemptId = () =>
    (creationAttemptIdRef.current ??=
      globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)

  const creationAttemptModeRef = useRef<AppletCreationMode | null>(null)
  const creationAttemptMode = (): AppletCreationMode => creationAttemptModeRef.current ?? "classic"
  const trackCreationStarted = (creationMode: AppletCreationMode) => {
    if (state.editing || creationAttemptModeRef.current) return
    creationAttemptModeRef.current = creationMode
    trackAppletCreationStarted({ creationMode, creationAttemptId: creationAttemptId(), entrypoint: "diy_composer" })
  }

  // A prefill visit defers the creation-started event to the chat intro submit.
  // If the user switches to Classic instead, the preloaded template is what they
  // build from, so latch "template" as soon as the classic pane takes over.
  useEffect(() => {
    if (creationModeOption !== creationModeOptions.DIY) return
    if (chatPrefill && templateData && !importedState) trackCreationStarted("template")
  }, [creationModeOption])

  const hasTrackedPreviewShownRef = useRef(false)
  useEffect(() => {
    if (state.editing || state.step !== steps.PREVIEW) {
      hasTrackedPreviewShownRef.current = false
      return
    }
    if (hasTrackedPreviewShownRef.current) return
    hasTrackedPreviewShownRef.current = true

    trackAppletCreatePreviewShown({
      creationMode: creationAttemptMode(),
      creationAttemptId: creationAttemptId(),
      entrypoint: "diy_composer",
    })
  }, [state.step, state.editing])

  useEffect(() => {
    // A prefill must land in a fresh chat session, never resume an unsettled one.
    if (chatPrefill) clearStoredConversation(window.sessionStorage)
    // importedState is for editing an existing Applet, so templates shouldn't be compatible
    if (templateData && !importedState) {
      // When chat consumes the prefill, the attempt is tracked as "try_it" on intro
      // submit; latching "template" here would mislabel the whole attempt.
      if (!chatPrefill) trackCreationStarted("template")
      // templates are for linking from public pages, we don't need to load private services first
      loadTemplateData(preloadedServices, templateData, dispatch)
    }
    const effect = async () => {
      const privateServices = await loadPrivateServices()
      const allServices = privateServices.concat(preloadedServices)
      const suggestedServices = serviceSuggestion
        ? allServices.filter((s: { module_name: string }) => s.module_name === serviceSuggestion)
        : []
      onServicesLoaded(uniqBy(suggestedServices.concat(allServices), (s: { module_name: string }) => s.module_name))
      if (!state.editing) {
        window.App.Utils?.logCustomDatadogAction?.("diy_applet_maker_tab")
      }
      dispatch({ type: "LOADING", value: false })
    }
    effect()
  }, [])

  useEffect(() => {
    if (!user.creditsBilling) return

    fetchCredits()
      .then(value => dispatch({ type: "CREDITS_LOADED", value }))
      .catch(() => {
        // Without a balance the credit UI stays hidden; the Applet is still buildable.
      })
  }, [user.creditsBilling])

  useEffect(() => {
    if (user.permissions?.applet_data_store?.permitted) {
      graphQueryBang(AppletDataStoreQuery)
        .then((result: { me?: { applet_data_store?: Record<string, unknown> } }) => {
          const vars = result.me?.applet_data_store
          if (vars && typeof vars === "object") {
            setPersistedDataStore(Object.keys(vars))
          }
        })
        .catch(() => {
          // Silently fail if data store can't be fetched
        })
    }
  }, [user.permissions?.applet_data_store?.permitted])

  const filterCodeDataStore = useMemo(() => parseFilterCodeDataStore(state.filterCode), [state.filterCode])

  const onServicesLoaded = (services: Array<Service>) => {
    dispatch({
      type: "SERVICES_LOADED",
      value: services,
    })

    if (importedState) {
      const { id, normalized_applet, applet_trigger, applet_actions, applet_queries } = importedState

      const trigger = applet_trigger.trigger
      const actions = applet_actions.map(a => a.action)
      const queries = applet_queries.map(q => q.query)

      const formatStepForOverride = (step: TQAType, type: string) => {
        let owner = `/${pluralizeStepType(type)}/${step.full_module_name}`
        if (type === "action") {
          const index = actions.filter(a => a.full_module_name === step.full_module_name).indexOf(step as Action)
          owner += `/${index}`
        }

        let fields: { [key: string]: object } = {}
        const storedFields = normalized_applet.stored_fields.filter(f => {
          return f.owner === owner
        })

        const liveChannelField = storedFields.find(sf => sf.name === "_live_channel_id")
        if (liveChannelField) {
          fields["_live_channel_id"] = liveChannelField
        }

        fields = step[`${type}_fields`].reduce(
          (memo: { [key: string]: object }, f: { name: string }, order: number) => {
            const storedField = storedFields.find(sf => sf.name === f.name)

            if (!storedField) {
              memo[f.name] = {
                ...f,
                order,
                hidden: true, // this property is required on DiyFieldInput and isn't otherwise set
              }
              return memo
            }

            let currentValue = storedField.value
            if (storedField.is_hidden || storedField.value === null) {
              currentValue = storedField.default_value
            }

            memo[f.name] = {
              ...f,
              order,
              hidden: storedField.is_hidden,
              owner: storedField.owner,
              value: currentValue,
            }
            return memo
          },
          fields
        )

        const service: Service = services.find(s => s.id === step.channel.id) as Service
        service.allow_multiple_live_channels = step.channel.allow_multiple_live_channels
        service.live_channels = step.channel.live_channels

        const permission = normalized_applet.permissions.find(p => p.id === owner)
        step.live_channel_id = permission?.live_channel.id as string
        step.importedState = true

        return formatStep({ step, fields, smartFill: permission?.uses_ai_action_assist === true }, type, service)
      }

      dispatch({
        type: "SET_OVERRIDE_STATE",
        value: {
          appletId: id,
          name: normalized_applet.name,
          selectedTrigger: formatStepForOverride(trigger, "trigger"),
          selectedQueries: queries.map(q => formatStepForOverride(q, "query")),
          selectedActions: actions.map(a => formatStepForOverride(a, "action")),
          filterCode: importedState.filter_code,
          smartFillPrompt: importedState.ai_action_assist?.prompt ?? "",
          smartFillConfigured: !!importedState.ai_action_assist,
          pushEnabled: normalized_applet.push_enabled,
          delay: normalized_applet.actions_delay,
        },
      })
    }
  }

  // Check every time that trigger and first action changed.
  useEffect(() => {
    if (state.selectedTrigger !== null && state.selectedActions.length > 0) {
      const loadPreviewMutation = async () => {
        try {
          const response = await loadPreview({
            trigger: state.selectedTrigger.full_module_name,
            triggerFields: state.selectedTrigger.fields,
            triggerLiveChannelId: state.selectedTrigger.live_channel_id,
            action: state.selectedActions[0].full_module_name,
            actionFields: state.selectedActions[0].fields,
            actionLiveChannelId: state.selectedActions[0].live_channel_id,
          })

          updatePreview({
            name: response.statementPreview.normalized_applet.name || "",
            brandColor: response.statementPreview.normalized_applet.brand_color,
            serviceSlug: response.statementPreview.normalized_applet.service_slug,
          })
        } catch (error) {
          updatePreview({
            name: "",
            serviceSlug: state.selectedTrigger.service.module_name,
            brandColor: state.selectedTrigger.service.brand_color,
          })
        }
      }

      loadPreviewMutation()
    }
  }, [state.selectedTrigger, state.selectedActions[0]])

  const goBack = () => {
    dispatch({
      type: "CHANGE_STEP",
      value: previousStep(state),
    })
  }

  useEffect(() => {
    window.addEventListener("popstate", handlePopState)

    return function cleanup() {
      window.removeEventListener("popstate", handlePopState)
    }
  }, []) // Register listeners once

  const handlePopState = (event: Event) => {
    if (state.step !== steps.INITIAL) {
      event.preventDefault()
      goBack()
    }
  }

  useEffect(() => {
    if (state.dataChanged) {
      window.addEventListener("beforeunload", handleBeforeUnload, {
        once: true,
      })

      // cleanup will be calling in the next re-render execution. It doesn't happen when user finish DIY creation.
      // It's necessary to call manually to remove event listener when redirect user to applet page (using location.assign)
      return cleanupBeforeUnload
    }
  })

  // The stylesheet places the Zendesk launcher from this body class; the widget's
  // own close animation leaves inline sizes on it that must not follow to a step.
  useEffect(() => {
    const initial = state.step === steps.INITIAL
    document.body.classList.toggle("diy-step-initial", initial)

    const clearLauncherSizing = () => {
      const helpButton = document.querySelector<HTMLElement>("#zendesk-widget-button .help-button")
      if (!helpButton) return
      helpButton.style.width = ""
      helpButton.style.height = ""
      helpButton.style.transition = ""
    }

    if (!initial) clearLauncherSizing()

    return () => {
      document.body.classList.remove("diy-step-initial")
      clearLauncherSizing()
    }
  }, [state.step])

  const handleBeforeUnload = (event: Event) => {
    dispatch({ type: "LOADING", value: false })

    // If the user isn't explicitly saving the Applet, warn them before leaving the page
    if (!state.saving) {
      event.preventDefault()
      event.returnValue = "You have unsaved changes. Do you want to leave this page and discard your changes?"
    }
  }

  const cleanupBeforeUnload = function cleanup() {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  }

  const handlePageHide = () => {
    window.addEventListener("pagehide", event => {
      if (event.persisted) {
        dispatch({ type: "COMPLETE_RESET" })
      }
    })
  }
  const makeItYourOwnFeaturePrompt = async () => {
    const communityApplet = state.editing && !(userOwned || state.makingItTheirOwn)

    if (!communityApplet) {
      return Promise.resolve()
    }

    const showUpgradeModal = (step: string) => {
      updateUpgradeToPro({
        showModal: true,
        step,
      })
      return Promise.reject(needsUpgradeError)
    }

    const appletQuota = user.appletQuota.total == -1 ? Infinity : user.appletQuota.total

    const usesFilterCode = !!importedState?.filter_code
    const usesQueries = (importedState?.applet_queries?.length ?? 0) > 0

    if (
      (usesFilterCode && !user.permissions?.filter_code?.permitted) ||
      (usesQueries && !user.permissions?.queries?.permitted)
    ) {
      await showUpgradeModal("convert_pro_plus_applet")
    } else if (importedState.normalized_applet.intermediate_pro_features && !hasProFeatures(user.tier)) {
      await showUpgradeModal("multi_action")
    } else if (personalAppletsCount >= appletQuota) {
      await showUpgradeModal("applet_quota")
    }

    return new Promise((resolve: (value?: unknown) => void, reject: (reason?: unknown) => void) => {
      updateMakeItYourOwn({
        showModal: true,
        onConvert: () => {
          dispatch({
            type: "MAKING_IT_THEIR_OWN",
          })
          window.appendFlash("Converted to your own Applet", "success", resolve)
        },
        onCancel: () => reject(cancellationError),
      })
    })
  }

  const proFeatureGate = async (permissionName: string) => {
    const gatedFeatures = ["multi_service_account", "filter_code"]

    if (!gatedFeatures.includes(permissionName)) {
      return Promise.resolve()
    }

    if (user.permissions[permissionName]?.permitted) {
      return Promise.resolve()
    } else {
      updateUpgradeToPro({
        showModal: true,
        step: permissionName,
      })
      return Promise.reject(cancellationError)
    }
  }

  const updatePreview = (value: { name: string; serviceSlug: string; brandColor: string }) => {
    dispatch({
      type: "UPDATE_PREVIEW",
      value,
    })
  }

  const updateLoading = (value: boolean) => dispatch({ type: "LOADING", value })

  const selectingTandaFieldsProps = () => ({
    step: state.selectedStep,
    sourceAppletId: state.appletId,
    stepErrors: state.errors,
    editMode: state.editing,
    addingStep: state.addingStep,
    userOwned,
    userAllowMultipleLiveChannels: user.permissions.multi_service_account.permitted === true,
    makeItYourOwnFeaturePrompt,
    makingItTheirOwn: state.makingItTheirOwn,
    connectionFinishedUrl: urls.connectionFinishedUrl,
    changeServiceAccount: (live_channel_id: string) => dispatch({ type: "CHANGE_SERVICE_ACCOUNT", live_channel_id }),
    updateServiceAccountList: (live_channels: Array<LiveChannels>) =>
      dispatch({ type: "UPDATE_SERVICE_ACCOUNT_LIST", live_channels }),
    proFeatureGate,
  })

  const handleConfigureStepFields = (type: string, step: TQAType) => {
    const stepFields = step[`${type}_fields`] as UIStepField[]

    // On new mode (live_channel_id empty), choose first non-offline service
    if (!step.live_channel_id) {
      step.live_channel_id = step.service.live_channels?.find(lc => !lc.offline)?.id as string
    }

    // DIY new Applets doesn't have stored fields and need to build the memory object in JS.
    // For all other cases, it is provided in the stored fields by Web::ConnectSDKFlow.inject_field_for_service_accounts.
    if (
      !stepFields.some(sf => sf.field_ui_type === "service_account_select") &&
      state.connectingService.allow_multiple_live_channels
    ) {
      stepFields.unshift({
        field_ui_type: "service_account_select",
        helper_text: "",
        hideable: false,
        label: `${state.connectingService.name} account`,
        name: "_live_channel_id",
        order: -1,
        normalized_field_type: "option",
        required: true,
      })
    }

    if (stepFields.length > 0) {
      return dispatch({
        type: `SELECTING_${type.toUpperCase()}_FIELDS`,
        value: step,
      })
    }

    return dispatch({
      type: state.addingStep ? `${type.toUpperCase()}_ADDED` : `${type.toUpperCase()}_REPLACED`,
      value: { step, fields: [] },
    })
  }

  const handleStepSelect = async (type: string, step: TQAType) => {
    await applyFieldsAndIngredients(type, step)

    if (!isUserConnected(state.connectingService)) {
      return dispatch({
        type: `CONNECTING_${type.toUpperCase()}_SERVICE`,
        value: step,
      })
    }

    handleConfigureStepFields(type, step)
  }

  const applyFieldsAndIngredients = async (type: string, step: TQAType) => {
    updateLoading(true)
    const fieldsType = `${type}_fields`
    let fieldsResponse

    try {
      fieldsResponse = await fetchStepFieldsAndIngredients(type as StepType, step)
    } catch (e) {
      updateLoading(false)
      return
    }

    const {
      [type]: { ingredients, [fieldsType]: fields },
    } = fieldsResponse

    step[fieldsType] = fields
    if (ingredients) Object.assign(step, { ingredients })
    updateLoading(false)
  }

  const handleServiceConnection = (type: string, service: Service) => {
    dispatch({
      type: `${type.toUpperCase()}_SERVICE_CONNECTED`,
      live_channels: service.live_channels,
    })

    handleConfigureStepFields(type, {
      ...state.selectedStep,
      needsConnection: false, // service_connected
      service: {
        ...state.selectedStep.service,
        live_channels: service.live_channels,
      },
    })
  }

  const logStepAdded = (name: string, data?: { [key: string]: string }) => {
    // only log when creating a new applet
    if (!state.editing) {
      window.App.Utils?.logCustomDatadogAction?.(name, data)
    }
  }

  const DIYCreationMode = creationModeOption == creationModeOptions.DIY

  // The first time the Classic tab is active with the services loaded, warm
  // the cache for the service selector's recommended-service images. The ref
  // keeps tab switching from inserting the links again.
  const prefetchedServiceImagesRef = useRef(false)
  useEffect(() => {
    if (!DIYCreationMode || prefetchedServiceImagesRef.current || state.services.length === 0) return

    prefetchedServiceImagesRef.current = true
    prefetchRecommendedServiceImages(state.services, recommendedServices)
  }, [DIYCreationMode, state.services])

  // A template deep link loads content into Classic without the user doing anything, so
  // content alone can't mean "the user started building here". Only their first edit
  // (dataChanged) commits them to Classic and hides the switcher.
  const classicEmpty =
    !state.selectedTrigger &&
    state.selectedQueries.length == 0 &&
    state.selectedActions.length == 0 &&
    !state.filterCode
  const showSwitcher = !state.editing && (!DIYCreationMode || classicEmpty || !state.dataChanged)

  const chatShellActive = chatAvailable && state.step === steps.INITIAL && !DIYCreationMode

  const openUpgradeModal = (step: string, stepTier: Tier = "intermediate_pro") =>
    updateUpgradeToPro({ showModal: true, step, stepTier })

  const creditsReady = creditsLoaded(state)
  const showCreditBar =
    creditsReady &&
    state.step === steps.INITIAL &&
    state.creditsBalance.status !== "empty" &&
    canUpgradeCredits(user.tier)

  let currentStep
  switch (state.step) {
    case steps.INITIAL:
      currentStep = (
        <>
          {!state.editing && (
            <AppletLimitWarning
              user={user}
              className="initial-step"
              onUpgrade={() =>
                updateUpgradeToPro({
                  showModal: true,
                  step: "applet_quota_upsell",
                  stepTier: user.tier === "free" ? "intermediate_pro" : "pro",
                })
              }
            />
          )}
          {showCreditBar && (
            // No-op until the credits checkout modal is implemented (https://app.shortcut.com/ifttt/story/146926/web-plan-upgrade-checkout-credit-model).
            <CreditBalanceBar balance={state.creditsBalance} tier={user.tier} onUpgrade={() => {}} />
          )}
          {showSwitcher && chatAvailable && (
            <ul className={styles["creation-mode-switcher"]}>
              <li key="ai" className={classNames({ [styles.selected]: !DIYCreationMode })}>
                <button
                  type="button"
                  className="text-h5"
                  onClick={() => {
                    updateCreationModeOption(creationModeOptions.CHAT)
                  }}
                  {...(!state.editing && { "data-dd-action-name": "ai_applet_maker_tab" })}
                >
                  Chat
                </button>
              </li>
              <li key="classic" className={classNames({ [styles.selected]: DIYCreationMode })}>
                <button
                  type="button"
                  className="text-h5"
                  onClick={() => {
                    updateCreationModeOption(creationModeOptions.DIY)
                  }}
                >
                  Classic
                </button>
              </li>
            </ul>
          )}
          {chatAvailable && !state.editing && pendingHandOff && (
            <section
              className={styles["mode-containers"]}
              style={{
                display: DIYCreationMode ? "none" : chatShellActive ? "flex" : "block",
                flexDirection: "column",
              }}
            >
              <ChatTab
                user={user}
                active={!DIYCreationMode}
                introCopy={introCopy}
                initialPrompt={pendingHandOff.prompt?.prompt}
                autoStart={pendingHandOff.autoStart}
                entryPoint={
                  pendingHandOff.prompt ? ENTRY_POINT_BY_PROMPT_SOURCE[pendingHandOff.prompt.source] : undefined
                }
                chatPrefill={chatPrefill}
                trackCreationStarted={trackCreationStarted}
                creationAttemptId={creationAttemptId}
                creationAttemptMode={creationAttemptMode}
                showUpgradeModal={(request?: UpgradeRequest) =>
                  updateUpgradeToPro({
                    showModal: true,
                    step: request?.step ?? "ai_maker_rate_limit",
                    stepTier: request?.stepTier ?? "intermediate_pro",
                    onUpgrade: request?.onUpgrade,
                  })
                }
              />
            </section>
          )}
          <section className={styles["mode-containers"]} style={{ display: DIYCreationMode ? "block" : "none" }}>
            <InitialStep
              dispatch={dispatch}
              editing={state.editing}
              trackCreationStarted={trackCreationStarted}
              makeItYourOwnFeaturePrompt={makeItYourOwnFeaturePrompt}
              makingItTheirOwn={state.makingItTheirOwn}
              proFeatureGate={proFeatureGate}
              sourceAppletId={state.editing ? importedState.id : null}
              state={state}
              user={{
                tier: user.tier,
                permissions: user.permissions,
              }}
              userOwned={userOwned}
              onCreationSuccess={() => {
                // Remove warning about unsaved changes on redirect.
                cleanupBeforeUnload()
              }}
              showFilterCodeModal={() => {
                updateFilterCodeModal({ showModal: true })
              }}
              showUpgradeModal={(step: string) =>
                updateUpgradeToPro({
                  showModal: true,
                  step,
                })
              }
            />
          </section>
        </>
      )
      break
    case steps.SELECTING_TRIGGER_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading}
          updateLoading={updateLoading}
          services={state.services}
          recommendedServiceModuleNames={recommendedServices?.for_triggers?.map(({ module_name }) => module_name)}
          allServiceCategories={allServiceCategories}
          onServiceSelectQuery={DIYLoadServiceTriggersQuery}
          onServiceSelect={(updatedService: Service) =>
            dispatch({
              type: "SELECTING_TRIGGER",
              value: updatedService,
            })
          }
          stepType={"trigger"}
        />
      )
      break
    case steps.SELECTING_ACTION_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading}
          updateLoading={updateLoading}
          services={state.services}
          recommendedServiceModuleNames={recommendedServices?.for_actions?.map(({ module_name }) => module_name)}
          allServiceCategories={allServiceCategories}
          onServiceSelectQuery={DIYLoadServiceActionsQuery}
          onServiceSelect={(updatedService: Service) =>
            dispatch({
              type: "SELECTING_ACTION",
              value: updatedService,
            })
          }
          stepType={"action"}
        />
      )
      break
    case steps.SELECTING_QUERY_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading}
          updateLoading={updateLoading}
          services={state.services}
          recommendedServiceModuleNames={recommendedServices?.for_queries?.map(({ module_name }) => module_name)}
          allServiceCategories={allServiceCategories}
          onServiceSelectQuery={DIYLoadServiceQueriesQuery}
          onServiceSelect={(updatedService: Service) =>
            dispatch({
              type: "SELECTING_QUERY",
              value: updatedService,
            })
          }
          stepType={"query"}
        />
      )
      break
    case steps.CONNECTING_TRIGGER_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          connectionContext="trigger"
          onServiceConnected={(service: Service) => {
            handleServiceConnection("trigger", service)
          }}
        />
      )
      break
    case steps.CONNECTING_ACTION_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          connectionContext="action"
          onServiceConnected={(service: Service) => {
            handleServiceConnection("action", service)
          }}
        />
      )
      break
    case steps.CONNECTING_QUERY_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          connectionContext="query"
          onServiceConnected={(service: Service) => {
            handleServiceConnection("query", service)
          }}
        />
      )
      break
    case steps.SELECTING_TRIGGER:
      currentStep = (
        <StepSelector
          onStepSelect={(step: TQAType) => {
            handleStepSelect("trigger", step)
          }}
          selectedSteps={[]}
          steps={state.connectingService.public_triggers}
          stepType="trigger"
          service={state.connectingService}
        />
      )
      break
    case steps.SELECTING_ACTION:
      currentStep = (
        <StepSelector
          onStepSelect={(step: TQAType) => {
            handleStepSelect("action", step)
          }}
          selectedSteps={msa ? selectedActionsAboveLimit(state.selectedActions) : state.selectedActions}
          steps={state.connectingService.public_actions}
          stepType="action"
          service={state.connectingService}
        />
      )
      break
    case steps.SELECTING_QUERY:
      currentStep = (
        <StepSelector
          onStepSelect={(step: TQAType) => {
            handleStepSelect("query", step)
          }}
          selectedSteps={state.selectedQueries}
          steps={state.connectingService.public_queries}
          stepType="query"
          service={state.connectingService}
        />
      )
      break
    case steps.SELECTING_TRIGGER_FIELDS:
      currentStep = (
        <StepFieldsSelector
          {...selectingTandaFieldsProps()}
          onCreate={(step: TQAType, fields: object) => {
            logStepAdded("diy_applet_maker_trigger_selected", { service: step.service.module_name })
            dispatch({
              type: state.addingStep ? "TRIGGER_ADDED" : "TRIGGER_REPLACED",
              value: { step, fields },
            })
          }}
          stepFields={state.selectedStep.trigger_fields}
          stepType="trigger"
          proFeatureGate={proFeatureGate}
        />
      )
      break
    case steps.SELECTING_ACTION_FIELDS:
      currentStep = (
        <ErrorBoundary
          placeholder={<h3>Sorry, there was a problem getting these action details; we are investigating!</h3>}
        >
          <StepFieldsSelector
            {...selectingTandaFieldsProps()}
            smartFill={
              canSetUpSmartFill
                ? {
                    enabled: !!(state.selectedStep as Action).smartFill,
                    prompt: state.smartFillPrompt,
                    onAnotherAction: state.selectedActions.some(
                      (action: Action, index: number) =>
                        action.smartFill && (state.addingStep || index !== state.selectedStepIndex),
                    ),
                  }
                : undefined
            }
            onCreate={(step: TQAType, fields: object, smartFill?: { enabled: boolean; prompt: string }) => {
              logStepAdded(`diy_applet_maker_action_${state.selectedActions.length + 1}_selected`, {
                service: step.service.module_name,
              })
              dispatch({
                type: state.addingStep ? "ACTION_ADDED" : "ACTION_REPLACED",
                value: { step, fields, smartFill: smartFill?.enabled },
                smartFillPrompt: smartFill?.prompt,
              })
            }}
            stepFields={state.selectedStep.action_fields}
            stepType="action"
            ingredientsMetadata={ingredientsMetadataFromState(state, true, persistedDataStore, filterCodeDataStore)}
            trigger={state.selectedTrigger}
            queries={state.selectedQueries}
            defaultValueLoader={(step: Action, stepType: StepType) =>
              fetchStepDefaultValues(state.selectedTrigger?.full_module_name, step, stepType)
            }
          />
        </ErrorBoundary>
      )
      break
    case steps.SELECTING_QUERY_FIELDS:
      currentStep = (
        <StepFieldsSelector
          {...selectingTandaFieldsProps()}
          onCreate={(step: TQAType, fields: object) => {
            logStepAdded(`diy_applet_maker_query_${state.selectedQueries.length + 1}_selected`, {
              service: step.service.module_name,
            })
            dispatch({
              type: state.addingStep ? "QUERY_ADDED" : "QUERY_REPLACED",
              value: { step, fields },
            })
          }}
          stepFields={state.selectedStep.query_fields}
          stepType="query"
          ingredientsMetadata={ingredientsMetadataFromState(state, false, persistedDataStore, filterCodeDataStore)}
          trigger={state.selectedTrigger}
          defaultValueLoader={(step: Query, stepType: StepType) =>
            fetchStepDefaultValues(state.selectedTrigger?.full_module_name, step, stepType)
          }
        />
      )
      break
    case steps.ADDING_FILTER_CODE:
    case steps.EDITING_FILTER_CODE:
      currentStep = (
        <FilterCode
          contextTemplate={filterCodeTemplate}
          selectedTrigger={state.selectedTrigger}
          selectedQueries={state.selectedQueries}
          selectedActions={state.selectedActions}
          filterCode={state.filterCode}
          proFeatureGate={proFeatureGate}
          userPermissions={user.permissions}
          ctaSlot={headerActionSlot}
          updateFilterCode={(filterCode: string | null) => {
            if (!filterCode) {
              dispatch({ type: "FILTER_CODE_REMOVED" })
              return
            }

            logStepAdded("diy_applet_maker_filter_code_added")
            dispatch({ type: "FILTER_CODE_MODIFIED", value: filterCode })
          }}
        />
      )
      break
    case steps.ADDING_DELAY_ACTION:
    case steps.EDITING_DELAY_ACTION:
      currentStep = (
        <Delay
          delay={state.delay}
          updateDelay={delay => {
            logStepAdded("diy_applet_maker_delay_added")
            dispatch({ type: "DELAY_ACTION_MODIFIED", value: delay })
          }}
        />
      )
      break
    case steps.PREVIEW: {
      const datastoreTrackingProps = {
        hasFilterCode: !!state.filterCode,
        setDatastoreInFilterCode: filterCodeDataStore.length > 0,
        usedDatastoreValueIngredient: [...state.selectedActions, ...state.selectedQueries].some(
          (step: { fields?: Record<string, { value?: string }> }) =>
            step.fields && Object.values(step.fields).some(f => /\{\{DataStore\./.test(f.value || ""))
        ),
        datastoreKeysCount: filterCodeDataStore.length,
      }
      currentStep = (
        <Preview
          actions={state.selectedActions}
          errors={state.errors}
          userAtQuota={userAtQuota}
          showAppletQuotaModal={() => {
            updateUpgradeToPro({ showModal: true, step: "applet_quota" })
          }}
          upgradeWarningCopy={
            userAtQuota
              ? modalCopy({
                  step: "applet_quota",
                  currentTier: user.tier,
                  eligibleForTrial: user.eligibleForTrial,
                  eligibleTrialPeriod: user.eligibleTrialPeriod,
                })
              : {}
          }
          onCreationError={(errors: object) => {
            const creationMode = creationAttemptMode()
            const errorType = (errors as any)?.applet?.base?.[0]?.message || "applet_creation_failed"

            trackAppletCreationFailed({
              creationMode,
              creationAttemptId: creationAttemptId(),
              entrypoint: "diy_composer",
              errorType,
              ...datastoreTrackingProps,
            })

            dispatch({
              type: "STEP_CREATION_FAILED",
              value: errors,
            })
          }}
          onCreationSuccess={() => {
            const creationMode = creationAttemptMode()

            trackAppletCreationCompleted({
              creationMode,
              creationAttemptId: creationAttemptId(),
              entrypoint: "diy_composer",
              triggerModuleName: state.selectedTrigger?.full_module_name,
              ...datastoreTrackingProps,
            })

            // Remove warning about unsaved changes on redirect.
            cleanupBeforeUnload()
            // reset the state upon navigation to avoid bfcache issues navigating back after creation success
            handlePageHide()
            window.App.Utils?.logCustomDatadogAction?.("diy_applet_created", { user })
            window.App.Utils?.logCustomDatadogAction?.("diy_or_ai_applet_created", { user })
          }}
          preview={state.preview}
          queries={state.selectedQueries}
          trigger={state.selectedTrigger}
          filterCode={state.filterCode}
          canUseSmartFill={!!user.canUseSmartFill}
          smartFillPrompt={state.smartFillPrompt}
          userLogin={user.login}
          delay={state.delay}
          appletTier={state.appletTier}
          failedAppletRunNotificationsEnabled={state.failedAppletRunNotificationsEnabled}
          onFailedAppletRunNotificationsChange={(value: boolean) =>
            dispatch({ type: "FAILED_RUN_NOTIFICATIONS_CHANGED", value })
          }
        />
      )
      break
    }
  }

  const stepsToShowBgColor = [
    steps.CONNECTING_TRIGGER_SERVICE,
    steps.SELECTING_TRIGGER,
    steps.SELECTING_TRIGGER_FIELDS,

    steps.CONNECTING_QUERY_SERVICE,
    steps.SELECTING_QUERY,
    steps.SELECTING_QUERY_FIELDS,

    steps.CONNECTING_ACTION_SERVICE,
    steps.SELECTING_ACTION,
    steps.SELECTING_ACTION_FIELDS,
  ]

  const contextColor = () => {
    const bGColorService = state.selectedStep?.service || state.connectingService
    if (stepsToShowBgColor.includes(state.step) && bGColorService) {
      return bGColorService.brand_color
    } else if (stepsToShowBgColor.includes(state.step)) {
      return colors.primaryTextColor // Fallback in case any issue in flow with service
    } else if (state.step === steps.PREVIEW) {
      return Object.entries(state.preview).length ? state.preview.brandColor : "white"
    } else {
      return "white"
    }
  }

  const appletFeatureFlags = {
    pro_features: importedState?.normalized_applet?.pro_features,
    intermediate_pro_features: importedState?.normalized_applet?.intermediate_pro_features,
  }

  const showAppletQuotaBar =
    user.appletQuota.remaining > 0 && state.step === steps.INITIAL && !state.editing

  // Global marker: Solid Fill belongs to the steps that paint a brand color;
  // delay and filter code stay on white with the Outline treatment.
  const onFilterCodeStep = [steps.ADDING_FILTER_CODE, steps.EDITING_FILTER_CODE].includes(state.step)
  const sectionClassName = classNames(
    styles["diy-composer"],
    { "diy-on-color": contextColor() !== "white" },
    {
      [styles["filter-code-view"]]: onFilterCodeStep,
      [styles["has-bottom-bar"]]: showAppletQuotaBar || showCreditBar,
      [styles["chat-style"]]: chatAvailable && state.step === steps.INITIAL,
      [styles["classic-active"]]: chatAvailable && state.step === steps.INITIAL && DIYCreationMode,
    },
  )

  const shellClassName = classNames({ [styles["chat-shell"]]: chatAvailable && state.step === steps.INITIAL })

  return (
    <BackgroundColorContext.Provider value={contextColor()}>
      <MakeItYourOwnModal
        visible={makeItYourOwn.showModal}
        onConvert={makeItYourOwn.onConvert}
        onCancel={makeItYourOwn.onCancel}
        onClose={() => {
          if (makeItYourOwn.onClose) {
            makeItYourOwn.onClose()
          }
          updateMakeItYourOwn({
            showModal: false,
          })
        }}
      />
      <UpgradePlanModal
        appletFeatureFlags={appletFeatureFlags}
        onClose={() => {
          updateUpgradeToPro({ showModal: false, step: upgradeToPro.step })
        }}
        onUpgrade={(checkoutModalTarget: string) => {
          checkoutModalTarget === "intermediate_pro"
            ? dispatch({ type: "SUCCESSFUL_PRO_UPGRADE" })
            : dispatch({ type: "SUCCESSFUL_PRO_PLUS_UPGRADE" })
          if (upgradeToPro.onUpgrade) upgradeToPro.onUpgrade(checkoutModalTarget)
        }}
        proPlusProductId={proPlusProductId}
        step={upgradeToPro.step as CheckoutStep}
        tierRequirement={upgradeToPro.stepTier as string}
        trackLocation={
          state.editing
            ? "Applet Config"
            : creationModeOption === creationModeOptions.CHAT
              ? "AI Applet Creator"
              : "Classic Applet Creator"
        }
        urls={urls}
        user={{
          eligibleForTrial: user.eligibleForTrial,
          eligibleTrialPeriod: user.eligibleTrialPeriod,
          isTrialing: user.isTrialing,
          subscriptionPaymentType: user.subscriptionPaymentType,
          tier: user.tier,
        }}
        visible={upgradeToPro.showModal}
      />
      <FilterCodeModal
        visible={filterCodeModal.showModal}
        onClose={() => {
          updateFilterCodeModal({ showModal: false })
        }}
      />
      <div className={shellClassName || undefined}>
        <Header
          appletTier={state.appletTier}
          editing={state.editing}
          user={user}
          userAtQuota={userAtQuota}
          title={stepsTitle(state)}
          step={state.step}
          chatStyle={chatAvailable && state.step === steps.INITIAL}
          compact={onFilterCodeStep}
          creditsBalance={creditsReady ? state.creditsBalance : null}
          actionSlotRef={onFilterCodeStep ? setHeaderActionSlot : undefined}
          backButtonTarget={goBack}
          showingCheckoutModal={upgradeToPro.showModal}
          showUpgradeModal={openUpgradeModal}
        />

        {state.step === steps.INITIAL && state.editing && userOwned ? (
          publishedByCurrentUser ? (
            <h6 className="published-applet-cta">
              Published on {publishedAppletData.publishedAt}, enabled by {publishedAppletData.enabledCount}{" "}
              {publishedAppletData.enabledCount === 1 ? "person" : "people"}{" "}
              <a
                className="link-inline open-in-new"
                href={urls.publishedAppletEditPath}
                target="_blank"
                rel="noreferrer"
              >
                Edit Published Applet
              </a>
            </h6>
          ) : (
            <h6 className="published-applet-cta">
              Want to publish this Applet so anyone can use it?{" "}
              <a
                style={{ textDecoration: "underline" }}
                className="link-inline open-in-new"
                href={urls.publishedAppletEditPath}
                target="_blank"
                rel="noreferrer"
              >
                Click here
              </a>
            </h6>
          )
        ) : null}

        <section className={sectionClassName}>{state.loading ? <Spinner /> : currentStep}</section>
      </div>
    </BackgroundColorContext.Provider>
  )
}

export default DIY
